/**
 * What became of a record: the outcome of its farthest hop (ui/DESIGN.md, "Lenses": a table with how far each got).
 * A record whose invoice left finance but never reached the e-invoice operator is not "done": it failed at Finance.
 * The runs say which hop failed — a failed run of the step's data model whose window holds the step's document.
 * Pure, so `node --test src/stage/lenses/outcome.test.ts` runs it as is.
 */
import type { RecordList, RecordRow, Run } from '../../lib/api.ts';
import { noun } from '../../lib/nouns.ts';
import { systemName } from '../../lib/systems.ts';

export type OutcomeState = 'failed' | 'held' | 'overdue' | 'waiting' | 'done' | 'other';

export interface Outcome {
  state: OutcomeState;
  /** "failed at Finance · invoice", "invoice overdue", "waiting for invoice", "done". */
  text: string;
  /** The step the outcome is about (an index into list.steps; -1 = the record's own documents; null = none). */
  step: number | null;
}

/** A document's time sits inside a run's window give or take this much. */
const TOLERANCE = 5000;

/** A failed run that touched this data model when the document came. */
export function failedRunAt(t: number, cls: string, runs: Run[]): Run | null {
  return runs.find((r) => r.failed > 0 && r.classes.includes(cls) && t >= r.first - TOLERANCE && t <= r.last + TOLERANCE) ?? null;
}

/** The time the record's step deltas are measured from: its earliest document anywhere, so no step is before it. */
export function baseOf(row: RecordRow): number {
  const times = row.steps.filter((c): c is number => typeof c === 'number' && c > 0);
  return Math.min(row.first, ...times);
}

/** The steps whose document came inside a failed run: a red ✕ in that column. */
export function failedSteps(row: RecordRow, list: RecordList, runs: Run[]): Set<number> {
  const out = new Set<number>();
  list.steps.forEach((st, i) => {
    const t = row.steps[i];
    if (typeof t === 'number' && t > 0 && failedRunAt(t, st.cls, runs)) out.add(i);
  });
  return out;
}

/**
 * The farthest hop decides: the latest document of the record (at a step or of its own kind) that failed makes
 * the record "failed at System · thing"; a step that never came is overdue, one not due yet is waited for;
 * otherwise the record is done (or whatever its own status says).
 */
export function outcomeOf(row: RecordRow, list: RecordList, runs: Run[]): Outcome {
  const failed = failedSteps(row, list, runs);
  const hops: { step: number; cls: string; system: string; t: number; failed: boolean; held: boolean }[] = list.steps.map((st, i) => {
    const t = row.steps[i];
    return typeof t === 'number' && t > 0 ? { step: i, cls: st.cls, system: st.system, t, failed: failed.has(i), held: false } : null;
  }).filter((h): h is NonNullable<typeof h> => h !== null);
  hops.push({ step: -1, cls: list.cls, system: list.system, t: row.last, failed: row.status === 'FAILED' || (row.failed > 0 && row.status !== 'SUCCESS'), held: row.status === 'HOLD' });
  const farthest = hops.sort((a, b) => b.t - a.t)[0];
  if (farthest?.failed) return { state: 'failed', text: `failed at ${systemName(farthest.system)} · ${noun(farthest.cls)}`, step: farthest.step };
  if (farthest?.held) return { state: 'held', text: `on hold at ${systemName(farthest.system)} · ${noun(farthest.cls)}`, step: farthest.step };
  const missing = row.steps.findIndex((c) => c === 'missing');
  if (missing >= 0) return { state: 'overdue', text: `${noun(list.steps[missing].cls)} overdue`, step: missing };
  const pending = row.steps.findIndex((c) => c === 'pending');
  if (pending >= 0) return { state: 'waiting', text: `waiting for ${noun(list.steps[pending].cls)}`, step: pending };
  if (!row.status || row.status === 'SUCCESS' || row.status === 'NEW') return { state: 'done', text: 'done', step: null };
  if (row.status === 'PROCESSING') return { state: 'other', text: 'running', step: -1 };
  return { state: 'other', text: row.status.toLowerCase(), step: -1 };
}
